import { useEffect, useRef, useState } from 'react';
import { Upload, FileText, Image as ImageIcon, Download, Trash2, File as FileIcon } from 'lucide-react';
import { listMemberFiles, uploadMemberFile, deleteMemberFile, fileDownloadUrl, MemberFile } from '../api';
import { useToast } from '../../../context/ToastContext';
import EmptyState from '../../../components/EmptyState';

function fmtSize(n: number | null): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDay(d: string | null): string {
  if (!d) return '';
  const dd = new Date(String(d).replace(' ', 'T'));
  return isNaN(dd.getTime()) ? '' : dd.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}
function iconFor(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'heic'].includes(ext)) return ImageIcon;
  if (ext === 'pdf') return FileText;
  return FileIcon;
}

export default function MemberFiles({ memberId, isAdmin }: { memberId: number; isAdmin: boolean }) {
  const [files, setFiles] = useState<MemberFile[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const load = () => listMemberFiles(memberId).then((r) => setFiles(r.data)).catch(() => setFiles([]));
  useEffect(() => { load(); }, [memberId]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error('Archivo muy grande (máx 15 MB)'); return; }
    setUploading(true);
    try {
      await uploadMemberFile(memberId, file);
      toast.success('Archivo subido');
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo subir');
    } finally { setUploading(false); }
  };

  const remove = async (f: MemberFile) => {
    if (!window.confirm(`¿Eliminar "${f.original_name}"?`)) return;
    setFiles((prev) => prev?.filter((x) => x.file_id !== f.file_id) || null);
    try { await deleteMemberFile(memberId, f.file_id); } catch { toast.error('No se pudo eliminar'); load(); }
  };

  return (
    <div className="mt-5">
      {isAdmin && (
        <div className="mb-4">
          <button onClick={() => inputRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent transition hover:opacity-90 disabled:opacity-50">
            <Upload size={15} /> {uploading ? 'Subiendo…' : 'Subir archivo'}
          </button>
          <input ref={inputRef} type="file" className="hidden" onChange={onPick}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt" />
          <p className="mt-1.5 text-xs text-content-3">PDF, imágenes, Word, Excel o texto · máx 15 MB</p>
        </div>
      )}

      {files === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-skeleton" />)}</div>
      ) : files.length === 0 ? (
        <EmptyState icon={FileText} title="Sin archivos" subtitle={isAdmin ? 'Sube identificaciones, consentimientos u otros documentos del alumno.' : 'Este alumno no tiene archivos.'} />
      ) : (
        <ul className="space-y-2">
          {files.map((f) => {
            const Icon = iconFor(f.original_name);
            return (
              <li key={f.file_id} className="flex items-center gap-3 rounded-xl border border-line border border-line bg-canvas px-3 py-2.5">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-blue/10 text-brand-blue"><Icon size={18} /></span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-content">{f.original_name}</div>
                  <div className="truncate text-xs text-content-3">
                    {fmtSize(f.size)}{f.created_at ? ` · ${fmtDay(f.created_at)}` : ''}{f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ''}
                  </div>
                </div>
                <a href={fileDownloadUrl(memberId, f.file_id)} target="_blank" rel="noreferrer" title="Descargar"
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-content-2 transition hover:bg-surface hover:text-accent-ink">
                  <Download size={16} />
                </a>
                {isAdmin && (
                  <button onClick={() => remove(f)} title="Eliminar"
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-content-3 transition hover:bg-surface hover:text-red-500">
                    <Trash2 size={16} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
